/**
 * YouTubeImportButton — a focused, one-click flow for importing a
 * YouTube video into Storylo Studio. Sits in the upload card next to
 * the generic "Fetch" URL button so YouTube has a clear front door.
 *
 * Why a dedicated button? YouTube actively blocks downloads from
 * cloud-server IPs (since early 2026, "Sign in to confirm you're not
 * a bot"), so a naive "paste URL → fetch" almost always fails for
 * YouTube specifically. This modal:
 *
 *   1. Tries the auto-download first (works ~1-in-3 for older videos
 *      and YouTube Shorts — worth a shot).
 *   2. If it fails, guides the user through the 30-second manual
 *      workaround without recommending sketchy third-party sites
 *      (browser native "Save Video As", a YouTube Premium download,
 *      or any reputable downloader of their choice).
 *   3. Auto-fills the parent component's video file slot when the
 *      user uploads the saved MP4 — so the rest of the Studio flow
 *      (script extraction, dubbing, publishing) continues seamlessly.
 *
 * Callbacks:
 *   onFileSelected(file)   — File picked from the manual-upload step
 *   onFetched(metadata)    — Auto-download succeeded; payload matches
 *                            the /api/fetch-url response shape so the
 *                            parent's existing videoFetched handler
 *                            can swallow it unmodified.
 */
import React, { useState } from "react";
import axios from "axios";
import { Youtube, X, Loader2, Upload, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function YouTubeImportButton({ onFileSelected, onFetched }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState("");
  const [stage, setStage] = useState("input"); // input | trying | blocked | success

  const tryAutoDownload = async () => {
    setError("");
    if (!url.trim()) {
      setError("Paste a YouTube URL first.");
      return;
    }
    if (!/youtube\.com|youtu\.be/i.test(url)) {
      setError("That doesn't look like a YouTube URL. For other sites, use the regular URL field.");
      return;
    }
    setFetching(true);
    setStage("trying");
    try {
      const { data } = await axios.post(
        `${API}/fetch-url`,
        { url: url.trim(), kind: "video" },
        { timeout: 5 * 60 * 1000 },
      );
      if (onFetched) onFetched(data);
      setStage("success");
      setTimeout(() => close(), 1200);
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
        e?.message ||
        "YouTube blocked the download.",
      );
      setStage("blocked");
    } finally {
      setFetching(false);
    }
  };

  const close = () => {
    setOpen(false);
    setUrl(""); setError(""); setStage("input"); setFetching(false);
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (file && onFileSelected) {
      onFileSelected(file);
      close();
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-sm whitespace-nowrap flex items-center gap-2 px-3 py-2 rounded-lg border border-red-500/40 text-red-200 bg-red-500/[0.06] hover:bg-red-500/[0.12] hover:border-red-500/60 transition-colors"
        data-testid="open-youtube-import"
        title="Import a YouTube video into Storylo"
      >
        <Youtube className="w-4 h-4" /> YouTube
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={close}
          data-testid="youtube-import-modal"
        >
          <div
            className="w-full max-w-lg panel p-6 md:p-7 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={close}
              className="absolute top-3 right-3 text-[color:var(--muted)] hover:text-white p-1"
              aria-label="Close"
              data-testid="youtube-import-close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <Youtube className="w-6 h-6 text-red-400" />
              <h3 className="font-display text-xl">Import from YouTube</h3>
            </div>

            {stage === "input" && (
              <>
                <p className="text-sm text-[color:var(--muted)] mb-4 leading-relaxed">
                  Paste a YouTube URL below. We&apos;ll try to download it for you — if YouTube blocks our server, we&apos;ll walk you through saving the file in 30 seconds.
                </p>
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=…"
                  className="input-base w-full px-3 py-2.5 text-sm mb-3"
                  data-testid="youtube-url-input"
                  autoFocus
                />
                {error && (
                  <p className="text-xs text-red-300 mb-3" data-testid="youtube-error">
                    {error}
                  </p>
                )}
                <button
                  type="button"
                  onClick={tryAutoDownload}
                  disabled={fetching || !url.trim()}
                  className="btn-primary w-full py-2.5 text-sm flex items-center justify-center gap-2"
                  data-testid="youtube-try-fetch"
                >
                  Try auto-download
                </button>
              </>
            )}

            {stage === "trying" && (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-[color:var(--accent)]" />
                <p className="text-sm mt-3">Downloading from YouTube…</p>
                <p className="text-[11px] text-[color:var(--muted)] mt-2">
                  This may take a minute. Keep this window open.
                </p>
              </div>
            )}

            {stage === "success" && (
              <div className="text-center py-8">
                <Check className="w-10 h-10 mx-auto text-emerald-400" />
                <p className="text-sm mt-3">Imported! Returning to Studio…</p>
              </div>
            )}

            {stage === "blocked" && (
              <>
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.06] p-3 mb-4">
                  <p className="text-xs text-amber-200 leading-relaxed">
                    <strong>YouTube blocked the auto-download.</strong> This is expected — YouTube blocks most cloud-server downloads. The 30-second workaround below always works.
                  </p>
                </div>
                <ol className="text-sm space-y-3 mb-5 leading-relaxed">
                  <li>
                    <span className="font-semibold text-white">1.</span>{" "}
                    Open the YouTube video on your computer
                    {url && (
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-2 text-[color:var(--accent)] underline text-xs"
                        data-testid="youtube-open-link"
                      >
                        → Open
                      </a>
                    )}
                  </li>
                  <li>
                    <span className="font-semibold text-white">2.</span>{" "}
                    Save the MP4 to your computer. Easiest paths:
                    <ul className="text-[11px] text-[color:var(--muted)] mt-1.5 ml-4 space-y-1 list-disc">
                      <li>If it&apos;s your own video: download from YouTube Studio (highest quality)</li>
                      <li>If you have YouTube Premium: tap &ldquo;Download&rdquo; on the video</li>
                      <li>Otherwise: use any video downloader you trust</li>
                    </ul>
                  </li>
                  <li>
                    <span className="font-semibold text-white">3.</span>{" "}
                    Drop the MP4 here ↓
                  </li>
                </ol>
                <label
                  className="block border-2 border-dashed border-white/15 hover:border-[color:var(--accent)]/60 rounded-lg p-5 text-center cursor-pointer transition-colors"
                  data-testid="youtube-fallback-upload"
                >
                  <Upload className="w-5 h-5 mx-auto text-[color:var(--muted)] mb-2" />
                  <span className="text-sm font-medium">Choose the saved MP4</span>
                  <span className="text-[11px] text-[color:var(--muted)] block mt-1">
                    MP4, MOV, MKV, or WebM
                  </span>
                  <input
                    type="file"
                    accept="video/*"
                    className="hidden"
                    onChange={handleFile}
                  />
                </label>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
